// src/app/context/AuthContext.jsx
/**
 * AuthProvider Context
 * ---------------------
 * This file sets up global authentication context using Firebase Auth and Firestore.
 * It provides the following methods and values to the application via React context:
 *
 * - `register`: Email/password registration with user document creation
 * - `login`: Email/password login
 * - `logout`: Firebase sign-out
 * - `googleSignIn`: Google OAuth login with Firestore document check
 * - `currentUser`: Currently authenticated user, including custom claims like roles
 *
 * This context wraps the app, subscribing to auth state changes and injecting auth utilities.
 */

import React, { useContext, useEffect, useState, createContext } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendEmailVerification,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import { auth, db } from "../../firebase"; // Firebase config
import { doc, setDoc, getDoc } from "firebase/firestore";

// ---------------------------------------------
// 1. Create a Context
// ---------------------------------------------
const AuthContext = createContext();

// ---------------------------------------------
// 2. Hook to Access Auth Context
// ---------------------------------------------
export const useAuth = () => useContext(AuthContext);

// ---------------------------------------------
// 3. Context Provider Component
// ---------------------------------------------
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null); // Track logged-in user
  const [loading, setLoading] = useState(true); // Prevent render until auth is initialized

  // ---------------------------------------------
  // 4. Google OAuth Sign-In Flow
  // ---------------------------------------------
  const googleSignIn = async () => {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    const user = result.user;

    // Check if this Google account is already registered in Firestore
    const userRef = doc(db, "users", user.uid);
    const userSnap = await getDoc(userRef);

    // Create new Firestore user document if it doesn't exist
    if (!userSnap.exists()) {
      await setDoc(userRef, {
        firstName: user.displayName?.split(" ")[0] || "",
        lastName: user.displayName?.split(" ").slice(1).join(" ") || "",
        email: user.email,
        title: "N/A",
        phone: user.phoneNumber || "",
        createdAt: new Date(),
      });
    }

    return user;
  };

  // ---------------------------------------------
  // 5. Email/Password Registration
  // ---------------------------------------------
  const register = async ({
    email,
    password,
    firstName,
    lastName,
    title,
    phone,
  }) => {
    // Create Firebase Auth user
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );
    const user = userCredential.user;

    // Send email verification
    await sendEmailVerification(user);

    // Create Firestore user profile
    await setDoc(doc(db, "users", user.uid), {
      firstName,
      lastName,
      title,
      email,
      phone,
      createdAt: new Date(),
    });

    return user;
  };

  // ---------------------------------------------
  // 6. Email/Password Login
  // ---------------------------------------------
  const login = async ({ email, password }) => {
    return await signInWithEmailAndPassword(auth, email, password);
  };

  // ---------------------------------------------
  // 7. Logout
  // ---------------------------------------------
  const logout = () => signOut(auth);

  // ---------------------------------------------
  // 8. Listen to Firebase Auth State Changes
  // ---------------------------------------------
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Get fresh ID token (with custom claims if present)
          const idTokenResult = await user.getIdTokenResult(true);
          const claims = idTokenResult.claims;

          // Extract roles if structured that way
          const rolesFromArray = claims.roles || [];

          // Also collect all flat claim keys (admin, town names, etc.)
          const flatRoles = Object.entries(claims)
            .filter(
              ([key, value]) => typeof value === "boolean" && value === true
            )
            .map(([key]) => key);

          // Combine both (avoid duplicates)
          const combinedRoles = Array.from(
            new Set([...rolesFromArray, ...flatRoles])
          );

          setCurrentUser({
            ...user,
            claims, // full claims object (optional to expose)
            roles: combinedRoles, // clean merged roles list
            lastLogin: user.metadata?.lastLoginAt || null,
          });
        } catch (err) {
          console.error("❌ Error getting token claims:", err);
          setCurrentUser({
            ...user,
            claims: {},
            roles: [],
          });
        }
      } else {
        setCurrentUser(null);
      }

      setLoading(false); // Finished loading after first auth check
    });

    // Cleanup subscription on unmount
    return unsubscribe;
  }, []);

  // ---------------------------------------------
  // 9. Context Value (available to children)
  // ---------------------------------------------
  const value = {
    currentUser,
    register,
    login,
    logout,
    googleSignIn,
    loading,
  };

  // ---------------------------------------------
  // 10. Provide Auth Context to the App
  // ---------------------------------------------
  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
